import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageSquareText, ChevronsLeftRight } from "lucide-react";
import { EASE, IMAGES, PHONE, PHONE_RAW, scrollToId } from "./shared";

const LINES = [
  { size: "", segs: [{ text: "YOUR STUMP.", cls: "text-bone" }] },
  {
    size: "whitespace-nowrap",
    segs: [
      { text: "GONE BY ", cls: "text-transparent [-webkit-text-stroke:2px_#f59e0b]" },
      { text: "FRIDAY.", cls: "italic text-amber-500" },
    ],
  },
];

function SawdustCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let raf;
    const isMobile = window.innerWidth < 640;
    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);
    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const CHIP_COLORS = ["#f59e0b", "#d97706", "#b45309", "#92400e", "#e7c489"];
    const parts = Array.from({ length: isMobile ? 14 : 38 }, () => ({
      x: Math.random(),
      y: Math.random(),
      len: Math.random() * 6 + 3,
      wid: Math.random() * 1.6 + 0.8,
      v: Math.random() * 0.0005 + 0.00015,
      sway: Math.random() * 1.4 + 0.4,
      phase: Math.random() * Math.PI * 2,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.02,
      o: Math.random() * 0.4 + 0.15,
      c: CHIP_COLORS[Math.floor(Math.random() * CHIP_COLORS.length)],
    }));
    let bursts = [];
    const spawnBurst = () => {
      const cx = Math.min(w * 0.24, 460);
      const cy = w < 640 ? h * 0.16 : h * 0.44;
      const speed = Math.max(0.55, Math.min(1, w / 1100));
      for (let i = 0; i < 22; i++) {
        bursts.push({
          x: cx + (Math.random() - 0.5) * 30,
          y: cy + (Math.random() - 0.5) * 26,
          vx: (7 + Math.random() * 6) * speed,
          vy: -3.4 + Math.random() * 3.2,
          g: 0.08 + Math.random() * 0.05,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.45,
          len: 5 + Math.random() * 8,
          wid: 1 + Math.random() * 1.6,
          life: 1,
          decay: 0.0028 + Math.random() * 0.0035,
          c: CHIP_COLORS[Math.floor(Math.random() * CHIP_COLORS.length)],
        });
      }
    };
    let t = 0;
    const tick = () => {
      t += 1;
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.y += p.v;
        p.rot += p.vr;
        if (p.y > 1.02) {
          p.y = -0.02;
          p.x = Math.random();
        }
        const px = p.x * w + Math.sin(t * 0.008 * p.sway + p.phase) * 14;
        const py = p.y * h;
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(p.rot);
        ctx.globalAlpha = p.o;
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.len / 2, -p.wid / 2, p.len, p.wid);
        ctx.restore();
      }
      for (let i = bursts.length - 1; i >= 0; i--) {
        const b = bursts[i];
        b.vy += b.g;
        b.vx *= 0.997;
        b.x += b.vx;
        b.y += b.vy;
        b.rot += b.vr;
        b.life -= b.decay;
        if (b.life <= 0 || b.y > h + 20 || b.x > w + 20) {
          bursts.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot);
        ctx.globalAlpha = Math.max(0, b.life) * 0.9;
        ctx.fillStyle = b.c;
        ctx.fillRect(-b.len / 2, -b.wid / 2, b.len, b.wid);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };
    const burstTimers = [1450, 1650, 1900].map((d) => setTimeout(spawnBurst, d));
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) tick();
    return () => {
      cancelAnimationFrame(raf);
      burstTimers.forEach(clearTimeout);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />;
}

function BeforeAfter() {
  const ref = useRef(null);
  const dragging = useRef(false);
  const touched = useRef(false);
  const [pos, setPos] = useState(100);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPos(45);
      return;
    }
    const timer = setTimeout(() => {
      const start = performance.now();
      const tick = (now) => {
        if (touched.current) return;
        const p = Math.min(1, (now - start) / 1800);
        const eased = 1 - Math.pow(1 - p, 3);
        setPos(100 - eased * 55);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const setFromClientX = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="hero-before-after"
      role="slider"
      aria-label="Before and after stump grinding comparison"
      aria-valuenow={Math.round(pos)}
      className="relative aspect-[4/5] w-full cursor-ew-resize touch-pan-y select-none"
      onPointerDown={(e) => {
        dragging.current = true;
        touched.current = true;
        ref.current.setPointerCapture(e.pointerId);
        setFromClientX(e.clientX);
      }}
      onPointerMove={(e) => {
        if (dragging.current) setFromClientX(e.clientX);
      }}
      onPointerUp={() => {
        dragging.current = false;
      }}
    >
      <img
        src={IMAGES.stumpAfter}
        alt="The same spot after Friday grinding — flat ground with fresh wood chips where the stump stood"
        className="absolute inset-0 h-full w-full object-cover contrast-110 saturate-[0.85]"
        draggable={false}
      />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={IMAGES.stumpBefore}
          alt="Backyard tree stump before grinding"
          className="absolute inset-0 h-full w-full object-cover contrast-110 saturate-[0.85]"
          draggable={false}
        />
      </div>

      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.7)]" />
        <span className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-amber-500 bg-[#0c0a09]/90 text-amber-500 backdrop-blur-sm">
          <ChevronsLeftRight className="h-5 w-5" />
        </span>
      </div>

      {pos > 22 && (
        <span className="absolute left-3 top-3 border border-stone-700/80 bg-[#0c0a09]/85 px-2 py-1 font-mono text-[10px] tracking-[0.25em] text-bone/90 backdrop-blur-sm">
          BEFORE
        </span>
      )}
      {pos < 78 && (
        <span className="absolute right-3 top-3 border border-amber-500/70 bg-[#0c0a09]/85 px-2 py-1 font-mono text-[10px] tracking-[0.25em] text-amber-500 backdrop-blur-sm">
          AFTER
        </span>
      )}
    </div>
  );
}

export default function Hero() {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [46, -46]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-[92vh] items-center overflow-hidden px-4 pb-16 pt-28 sm:px-8"
    >
      <SawdustCanvas />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(245,158,11,0.08),transparent_55%)]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500 sm:text-xs"
          >
            Eric Fisher · Columbus, OH · Stump Grinding Exclusively
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: EASE }}
                  className={`block ${line.size}`}
                >
                  {line.segs.map((s) => (
                    <span key={s.text} className={s.cls}>
                      {s.text}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg"
          >
            One day a week. No three-hour arrival windows. Snap a photo,
            send the address, and come home Friday to flat ground — all
            without disrupting your day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-quote-cta"
              onClick={() => scrollToId("quote")}
              className="group flex min-h-[48px] w-full items-center justify-center gap-2 bg-amber-500 px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 sm:w-auto sm:justify-start"
            >
              Get Friday Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-sms-cta"
              href={`sms:${PHONE_RAW}`}
              className="flex min-h-[48px] w-full items-center justify-center gap-2 border border-stone-700 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-stone-200 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500 sm:w-auto sm:justify-start"
            >
              <MessageSquareText className="h-4 w-4" />
              Text a photo to {PHONE}
            </a>
            <span
              data-testid="hero-price-badge"
              className="border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500"
            >
              From $125 / stump
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
          >
            <motion.div
              ref={cardRef}
              style={{ y }}
              data-testid="hero-dossier-card"
              className="relative border border-stone-800 bg-charcoal p-3 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
            >
              <span className="absolute -left-1.5 -top-1.5 z-10 font-mono text-sm text-amber-500/80">+</span>
              <span className="absolute -right-1.5 -top-1.5 z-10 font-mono text-sm text-amber-500/80">+</span>
              <span className="absolute -bottom-1.5 -left-1.5 z-10 font-mono text-sm text-amber-500/80">+</span>
              <span className="absolute -bottom-1.5 -right-1.5 z-10 font-mono text-sm text-amber-500/80">+</span>

              <div className="relative overflow-hidden">
                <BeforeAfter />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0c0a09]/90 via-transparent to-[#0c0a09]/30" />
                <p className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.2em] text-bone/70">
                  SUBJECT: 26″ RED OAK
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
